import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useOrpcTanstackQueryUtils, useOrpcClient } from '../orpc'
import type { DocumentCategory } from '@@api/shared'

// Get a single document by ID
export function useDocumentGetById(id: string | null) {
  const orpc = useOrpcTanstackQueryUtils()
  return useQuery({
    ...orpc.document.getById.queryOptions({ input: { id: id! } }),
    enabled: !!id,
  })
}

// Get all documents for a crew member
export function useDocumentGetByCrew(crewId: string) {
  const orpc = useOrpcTanstackQueryUtils()
  return useQuery({
    ...orpc.document.getByCrew.queryOptions({ input: { crewId } }),
    enabled: !!crewId,
  })
}

// Get all documents for a passenger
export function useDocumentGetByPassenger(passengerId: string) {
  const orpc = useOrpcTanstackQueryUtils()
  return useQuery(
    orpc.document.getByPassenger.queryOptions({ input: { passengerId } })
  )
}

// Get all documents (optionally filtered by category)
export function useDocumentGetAll(category?: DocumentCategory) {
  const orpc = useOrpcTanstackQueryUtils()
  return useQuery(
    orpc.document.getAll.queryOptions({
      input: category ? { category } : {},
    })
  )
}

// Get expiring documents
export function useDocumentGetExpiring(opts?: { category?: DocumentCategory; crewId?: string; passengerId?: string; daysAhead?: number }) {
  const orpc = useOrpcTanstackQueryUtils()
  return useQuery(
    orpc.document.getExpiring.queryOptions({
      input: {
        category: opts?.category,
        crewId: opts?.crewId,
        passengerId: opts?.passengerId,
        daysAhead: opts?.daysAhead,
      },
    })
  )
}

// Get expired documents
export function useDocumentGetExpired(opts?: { category?: DocumentCategory; crewId?: string; passengerId?: string }) {
  const orpc = useOrpcTanstackQueryUtils()
  return useQuery(
    orpc.document.getExpired.queryOptions({
      input: opts,
    })
  )
}

// Create a new document
export function useDocumentCreate() {
  const orpc = useOrpcTanstackQueryUtils()
  const queryClient = useQueryClient()

  return useMutation(
    orpc.document.create.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: orpc.document.key() })
        queryClient.invalidateQueries({ queryKey: orpc.crew.key() })
        queryClient.invalidateQueries({ queryKey: orpc.passenger.key() })
        queryClient.invalidateQueries({ queryKey: orpc.aircraft.key() })
      },
    })
  )
}

// Update a document
export function useDocumentUpdate() {
  const orpc = useOrpcTanstackQueryUtils()
  const queryClient = useQueryClient()

  return useMutation(
    orpc.document.update.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: orpc.document.key() })
        queryClient.invalidateQueries({ queryKey: orpc.crew.key() })
        queryClient.invalidateQueries({ queryKey: orpc.passenger.key() })
        queryClient.invalidateQueries({ queryKey: orpc.aircraft.key() })
      },
    })
  )
}

// Delete a document
export function useDocumentDelete() {
  const orpc = useOrpcTanstackQueryUtils()
  const queryClient = useQueryClient()

  return useMutation(
    orpc.document.delete.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: orpc.document.key() })
        queryClient.invalidateQueries({ queryKey: orpc.crew.key() })
        queryClient.invalidateQueries({ queryKey: orpc.passenger.key() })
        queryClient.invalidateQueries({ queryKey: orpc.aircraft.key() })
      },
    })
  )
}

// Search documents
export function useDocumentSearch(query: string, opts?: { category?: DocumentCategory; crewId?: string; passengerId?: string }) {
  const orpc = useOrpcTanstackQueryUtils()
  return useQuery({
    ...orpc.document.search.queryOptions({
      input: { query, category: opts?.category, crewId: opts?.crewId, passengerId: opts?.passengerId },
    }),
    enabled: query.length > 0,
  })
}

// Search compliance documents (admins see all, others only public)
export function useDocumentSearchCompliance(query?: string) {
  const orpc = useOrpcTanstackQueryUtils()
  return useQuery(
    orpc.document.searchCompliance.queryOptions({
      input: { query },
    })
  )
}

// Get multiple documents by IDs using Promise.all
export function useDocumentGetByIds(ids: string[] | null) {
  const client = useOrpcClient()
  return useQuery({
    queryKey: ['documents', 'byIds', ids],
    queryFn: async () => {
      if (!ids || ids.length === 0) return []

      const promises = ids.map((id) =>
        client.document.getById({ id })
      )

      return await Promise.all(promises)
    },
    enabled: !!ids && ids.length > 0,
  })
}
